import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DashboardStore, LoggerService, OrganisationService } from '@app/shared/data';
import { PanelModule } from 'primeng/panel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { interval } from 'rxjs';

@Component({
  selector: 'app-subscription-poller',
  templateUrl: './subscription-poller.html',

  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PanelModule, ProgressSpinnerModule],
})
export class SubscriptionPollerComponent implements OnInit {
  readonly isPolling = signal(true);
  readonly countdown = signal(15);
  readonly statusMessage = signal('Checking your subscription status...');

  private readonly _router = inject(Router);
  private readonly _logger = inject(LoggerService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _dashboardStore = inject(DashboardStore);
  private readonly _organisationService = inject(OrganisationService);

  ngOnInit(): void {
    this._logger.info('SubscriptionPollerComponent: Starting subscription monitoring');
    this.startSubscriptionMonitoring();
    this.startTimeout();
  }

  private startSubscriptionMonitoring(): void {
    const selectedOrgId = this._dashboardStore.selectedOrganisationId();

    if (!selectedOrgId) {
      this._logger.error('SubscriptionPollerComponent: No selected organization ID');
      this.redirectToDashboard();
      return;
    }

    // Set up real-time listener for organisation changes
    this._organisationService
      .getByID(selectedOrgId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (organisation) => {
          if (organisation?.hasActiveSubscription) {
            this._logger.info('SubscriptionPollerComponent: Active subscription detected, redirecting');
            this.statusMessage.set('Subscription activated! Redirecting...');
            this.isPolling.set(false);

            // Small delay to show success message
            setTimeout(() => {
              this.redirectToDashboard();
            }, 1500);
          } else {
            this._logger.info('SubscriptionPollerComponent: No active subscription yet, continuing to monitor');
          }
        },
        error: (error) => {
          this._logger.error('SubscriptionPollerComponent: Error monitoring subscription status', error);
          // Continue monitoring on error - might be temporary network issue
        },
      });
  }

  private startTimeout(): void {
    // Start countdown timer (update every second)
    interval(1000)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        const currentCount = this.countdown();
        if (currentCount > 0) {
          this.countdown.set(currentCount - 1);
        } else {
          this._logger.info('SubscriptionPollerComponent: Timeout reached, redirecting to dashboard');
          this.redirectToDashboard();
        }
      });
  }

  private redirectToDashboard(): void {
    this._router.navigateByUrl('/dashboard');
  }
}
